import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { api, apiError } from "@/lib/api";
import { UnlockModal } from "@/components/app/Achievements";
import {
  Loader2, Plus, X, Trophy, Users, Flame, Crown, Check, Lock, Globe,
  CalendarClock, ArrowLeft,
} from "lucide-react";

const CATS = ["career", "finances", "health", "fitness", "business", "education",
  "ai_skills", "productivity", "personal_growth", "travel", "relationships", "parenting"];

export default function Community() {
  const [tab, setTab] = useState("all");
  const [challenges, setChallenges] = useState(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [unlocked, setUnlocked] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/challenges?filter=${tab}`);
    setChallenges(data.challenges);
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  if (!challenges) return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#FF7E67]" /></div>;

  return (
    <div className="font-quicksand" data-testid="community-page">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="flex items-center gap-2 font-quicksand text-sm font-semibold text-[#FF7E67]">
            <Trophy className="h-4 w-4" strokeWidth={2.5} /> Community
          </p>
          <h1 className="mt-1 font-nunito text-4xl font-extrabold tracking-tight text-[#2D3748] sm:text-5xl">Challenges</h1>
          <p className="mt-2 font-quicksand text-base font-medium text-[#718096]">Grow together — join a challenge, climb the leaderboard, earn badges.</p>
        </div>
        <button onClick={() => setOpen(true)} data-testid="new-challenge-btn"
          className="flex items-center gap-2 rounded-full bg-sunrise px-5 py-3 font-nunito text-sm font-bold text-white shadow-glow-coral transition-transform duration-200 hover:scale-105">
          <Plus className="h-4 w-4" strokeWidth={2.5} /> New Challenge
        </button>
      </div>

      <div className="mb-6 inline-flex rounded-full border border-[#FFE8E3] bg-white p-1">
        {["all", "mine"].map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
            className={`rounded-full px-5 py-2 font-nunito text-sm font-bold capitalize transition-colors duration-200 ${tab === t ? "bg-[#FFE8E3] text-[#C2410C]" : "text-[#718096] hover:text-[#FF7E67]"}`}>
            {t === "all" ? "Discover" : "My Challenges"}
          </button>
        ))}
      </div>

      {challenges.length === 0 && (
        <div className="flex flex-col items-center rounded-[2rem] border-2 border-dashed border-[#FFE8E3] py-16 text-center">
          <Trophy className="h-10 w-10 text-[#FFB347]" strokeWidth={2} />
          <p className="mt-4 font-nunito text-lg font-bold text-[#2D3748]">No challenges yet</p>
          <p className="font-quicksand text-sm text-[#718096]">Create one and invite others to grow alongside you.</p>
        </div>
      )}

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {challenges.map((c) => (
          <ChallengeCard key={c.id} c={c} onOpen={() => setActive(c.id)} />
        ))}
      </div>

      {open && <CreateModal onClose={() => setOpen(false)} onCreated={() => { setOpen(false); setTab("mine"); load(); }} />}
      {active && <DetailModal cid={active} onClose={() => { setActive(null); load(); }} onUnlock={setUnlocked} />}
      {unlocked && <UnlockModal badges={unlocked} onClose={() => setUnlocked(null)} />}
    </div>
  );
}

function ChallengeCard({ c, onOpen }) {
  const pct = Math.min(100, Math.round((c.my_progress / c.target) * 100));
  return (
    <motion.button initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
      onClick={onOpen} data-testid={`challenge-card-${c.id}`}
      className="group rounded-[2rem] border border-[#FFE8E3] bg-white p-6 text-left shadow-warm transition-transform duration-200 hover:-translate-y-1">
      <div className="flex items-center justify-between">
        <span className="rounded-full bg-[#FFF4E6] px-3 py-1 font-nunito text-xs font-bold capitalize text-[#B45309]">{(c.category || "").replace("_", " ")}</span>
        <span className="flex items-center gap-1 font-quicksand text-xs font-semibold text-[#718096]">
          {c.visibility === "private" ? <Lock className="h-3.5 w-3.5" strokeWidth={2.5} /> : <Globe className="h-3.5 w-3.5" strokeWidth={2.5} />}
          {c.visibility}
        </span>
      </div>
      <h3 className="mt-3 font-nunito text-lg font-bold text-[#2D3748]">{c.title}</h3>
      {c.description && <p className="mt-1 line-clamp-2 font-quicksand text-sm text-[#718096]">{c.description}</p>}
      {c.joined ? (
        <div className="mt-4">
          <div className="h-2 w-full overflow-hidden rounded-full bg-[#FFE8E3]">
            <div className="h-full rounded-full bg-sunrise transition-all" style={{ width: `${pct}%` }} />
          </div>
          <p className="mt-1.5 font-quicksand text-xs font-semibold text-[#718096]">{c.my_progress}/{c.target} {c.metric_label} {c.completed && "• done!"}</p>
        </div>
      ) : (
        <p className="mt-4 font-quicksand text-sm font-semibold text-[#FF7E67]">Tap to join →</p>
      )}
      <div className="mt-4 flex items-center justify-between font-quicksand text-xs font-semibold text-[#718096]">
        <span className="flex items-center gap-1"><Users className="h-4 w-4" strokeWidth={2.5} />{c.participant_count}</span>
        <span className="flex items-center gap-1"><CalendarClock className="h-4 w-4" strokeWidth={2.5} />{c.status === "ended" ? "Ended" : `${c.days_left}d left`}</span>
      </div>
    </motion.button>
  );
}

function CreateModal({ onClose, onCreated }) {
  const [f, setF] = useState({ title: "", description: "", category: "fitness",
    visibility: "public", metric_label: "days", target: 30, duration_days: 30 });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });

  const submit = async () => {
    if (f.title.trim().length < 3) return toast.error("Give your challenge a title.");
    setLoading(true);
    try {
      const { data } = await api.post("/challenges", f);
      toast.success(data.join_code ? `Created! Share code: ${data.join_code}` : "Challenge created!");
      onCreated();
    } catch (e) { !e.handledUpgrade && toast.error(apiError(e.response?.data?.detail)); setLoading(false); }
  };

  const inputCls = "h-11 w-full rounded-2xl border border-[#FFE8E3] bg-[#FFF9F5] px-3 font-quicksand text-[#2D3748] outline-none transition-colors focus:border-[#FF7E67] focus:bg-white";

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-[#2D3748]/50 p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-lg space-y-3 rounded-[2rem] bg-white p-8 shadow-warm-lg" onClick={(e) => e.stopPropagation()} data-testid="create-challenge-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-nunito text-2xl font-extrabold text-[#2D3748]">New Challenge</h3>
          <button onClick={onClose} className="flex h-9 w-9 items-center justify-center rounded-full bg-[#FFF9F5] text-[#718096] hover:bg-[#FFE8E3]"><X className="h-5 w-5" /></button>
        </div>
        <input placeholder="Challenge title" value={f.title} onChange={(e) => set("title", e.target.value)} className={inputCls} data-testid="challenge-title-input" />
        <textarea placeholder="Description (optional)" rows={2} value={f.description} onChange={(e) => set("description", e.target.value)}
          className="w-full rounded-2xl border border-[#FFE8E3] bg-[#FFF9F5] p-3 font-quicksand text-[#2D3748] outline-none focus:border-[#FF7E67] focus:bg-white" />
        <div className="grid grid-cols-2 gap-3">
          <select value={f.category} onChange={(e) => set("category", e.target.value)} className={`${inputCls} capitalize`}>
            {CATS.map((c) => <option key={c} value={c}>{c.replace("_", " ")}</option>)}
          </select>
          <select value={f.visibility} onChange={(e) => set("visibility", e.target.value)} className={inputCls} data-testid="challenge-visibility">
            <option value="public">Public</option>
            <option value="private">Private (invite code)</option>
          </select>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <label className="font-quicksand text-xs font-semibold text-[#718096]">Unit
            <input value={f.metric_label} onChange={(e) => set("metric_label", e.target.value)} className={`${inputCls} mt-1`} />
          </label>
          <label className="font-quicksand text-xs font-semibold text-[#718096]">Target
            <input type="number" min="1" value={f.target} onChange={(e) => set("target", Number(e.target.value))} className={`${inputCls} mt-1`} />
          </label>
          <label className="font-quicksand text-xs font-semibold text-[#718096]">Days
            <input type="number" min="1" value={f.duration_days} onChange={(e) => set("duration_days", Number(e.target.value))} className={`${inputCls} mt-1`} />
          </label>
        </div>
        <button onClick={submit} disabled={loading} data-testid="create-challenge-submit"
          className="flex w-full items-center justify-center gap-2 rounded-full bg-sunrise py-3.5 font-nunito font-bold text-white shadow-glow-coral disabled:opacity-70">
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Launch Challenge"}
        </button>
      </div>
    </div>
  );
}

function DetailModal({ cid, onClose, onUnlock }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState("");

  const load = useCallback(async () => { const { data } = await api.get(`/challenges/${cid}`); setD(data); }, [cid]);
  useEffect(() => { load(); }, [load]);

  const join = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/challenges/${cid}/join`, { join_code: code });
      await load();
      if (data.unlocked?.length) onUnlock(data.unlocked);
      toast.success("You're in! 🎯");
    } catch (e) { !e.handledUpgrade && toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const checkin = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/challenges/${cid}/checkin`);
      await load();
      if (data.completed) toast.success("Challenge complete — you're a Champion! 🏆");
      else toast.success("Progress logged! Keep the streak alive.");
      if (data.unlocked?.length) onUnlock(data.unlocked);
    } catch (e) { !e.handledUpgrade && toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const leave = async () => { await api.post(`/challenges/${cid}/leave`); onClose(); };

  if (!d) return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-[#2D3748]/50 p-4 backdrop-blur-sm">
      <Loader2 className="h-8 w-8 animate-spin text-white" />
    </div>
  );
  const c = d.challenge;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-[#2D3748]/50 p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="max-h-[88vh] w-full max-w-lg overflow-y-auto rounded-[2rem] bg-white p-8 shadow-warm-lg" onClick={(e) => e.stopPropagation()} data-testid="challenge-detail-modal">
        <button onClick={onClose} className="mb-3 flex items-center gap-1 font-quicksand text-sm font-semibold text-[#718096] hover:text-[#FF7E67]"><ArrowLeft className="h-4 w-4" /> Back</button>
        <span className="rounded-full bg-[#FFF4E6] px-3 py-1 font-nunito text-xs font-bold capitalize text-[#B45309]">{(c.category || "").replace("_", " ")}</span>
        <h3 className="mt-3 font-nunito text-2xl font-extrabold text-[#2D3748]">{c.title}</h3>
        {c.description && <p className="mt-1 font-quicksand text-sm text-[#718096]">{c.description}</p>}
        <div className="mt-2 flex items-center gap-4 font-quicksand text-xs font-semibold text-[#718096]">
          <span className="flex items-center gap-1"><Users className="h-4 w-4" strokeWidth={2.5} />{c.participant_count} joined</span>
          <span className="flex items-center gap-1"><CalendarClock className="h-4 w-4" strokeWidth={2.5} />{c.status === "ended" ? "Ended" : `${c.days_left} days left`}</span>
          <span>Goal: {c.target} {c.metric_label}</span>
        </div>

        {c.join_code && (
          <p className="mt-3 rounded-2xl bg-[#FFF4E6] px-4 py-2 font-quicksand text-sm text-[#2D3748]">Invite code: <b className="font-nunito text-[#B45309]" data-testid="challenge-join-code">{c.join_code}</b></p>
        )}

        {/* Action */}
        {!c.joined ? (
          <div className="mt-5 space-y-2">
            {c.visibility === "private" && (
              <input placeholder="Enter invite code" value={code} onChange={(e) => setCode(e.target.value)} data-testid="join-code-input"
                className="h-11 w-full rounded-2xl border border-[#FFE8E3] bg-[#FFF9F5] px-3 font-quicksand uppercase text-[#2D3748] outline-none focus:border-[#FF7E67]" />
            )}
            <button onClick={join} disabled={busy} data-testid="join-challenge-btn"
              className="flex w-full items-center justify-center gap-2 rounded-full bg-sunrise py-3.5 font-nunito font-bold text-white shadow-glow-coral disabled:opacity-70">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Join Challenge"}
            </button>
          </div>
        ) : (
          <div className="mt-5 flex gap-3">
            <button onClick={checkin} disabled={busy || !d.can_checkin_today} data-testid="challenge-checkin-btn"
              className="flex flex-1 items-center justify-center gap-2 rounded-full bg-sunrise py-3.5 font-nunito font-bold text-white shadow-glow-coral disabled:opacity-60">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : d.can_checkin_today ? <><Check className="h-5 w-5" strokeWidth={2.5} /> Log Today</> : "Done for today ✓"}
            </button>
            <button onClick={leave} data-testid="leave-challenge-btn" className="rounded-full border border-[#FFE8E3] px-5 py-3.5 font-nunito text-sm font-bold text-[#718096] hover:border-[#FF7E67] hover:text-[#FF7E67]">Leave</button>
          </div>
        )}

        {/* Leaderboard */}
        <h4 className="mt-7 flex items-center gap-2 font-nunito text-lg font-bold text-[#2D3748]"><Crown className="h-5 w-5 text-[#FFB347]" strokeWidth={2.5} /> Leaderboard</h4>
        <div className="mt-3 space-y-2" data-testid="leaderboard">
          {d.leaderboard.map((l) => {
            const pct = Math.min(100, Math.round((l.progress / c.target) * 100));
            return (
              <div key={l.user_id} className={`flex items-center gap-3 rounded-2xl border p-3 ${l.is_me ? "border-[#FFB347] bg-[#FFF4E6]" : "border-[#FFE8E3] bg-[#FFF9F5]"}`}>
                <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full font-nunito text-sm font-extrabold ${l.rank === 1 ? "bg-sunrise text-white" : "bg-[#FFE8E3] text-[#B45309]"}`}>{l.rank}</span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-nunito text-sm font-bold text-[#2D3748]">{l.name}{l.is_me && " (you)"}{l.completed && " 🏆"}</p>
                  <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-[#FFE8E3]"><div className="h-full rounded-full bg-sunrise" style={{ width: `${pct}%` }} /></div>
                </div>
                <span className="shrink-0 font-nunito text-sm font-bold text-[#FF7E67]">{l.progress}</span>
              </div>
            );
          })}
        </div>

        {/* Feed */}
        {d.feed.length > 0 && (
          <>
            <h4 className="mt-7 flex items-center gap-2 font-nunito text-lg font-bold text-[#2D3748]"><Flame className="h-5 w-5 text-[#FF7E67]" strokeWidth={2.5} /> Activity</h4>
            <div className="mt-3 space-y-2">
              {d.feed.map((e) => (
                <p key={e.id} className="font-quicksand text-sm text-[#718096]"><b className="font-nunito text-[#2D3748]">{e.name}</b> {e.message}</p>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
